import type { ExportDestinationContent } from '@capture/shared';
import type { ErrorSummary } from './api/export-errors-client';
import styles from './export-composer.module.css';

interface DestinationListProps {
  destinations: ExportDestinationContent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  errorSummaries?: Map<string, ErrorSummary>;
}

export function DestinationList({ destinations, selectedId, onSelect, onAdd, errorSummaries }: DestinationListProps) {
  return (
    <>
      <div className={styles.listHeader}>DESTINATIONS</div>

      <ul className={styles.listItems} role="list">
        {destinations.map((dest) => {
          const summary = errorSummaries?.get(dest.id);
          const showBadge = summary !== undefined && summary.recordsWithErrors > 0;
          return (
            <li key={dest.id}>
              <button
                type="button"
                className={`${styles.listItem}${selectedId === dest.id ? ` ${styles.listItemActive}` : ''}`}
                onClick={() => onSelect(dest.id)}
              >
                <span className={styles.listItemName}>{dest.name}</span>
                <span className={styles.listItemMeta} data-testid="dest-item-meta">
                  {dest.adapter} · {dest.format.toUpperCase()}
                  {showBadge && (
                    <span
                      style={{
                        marginLeft: 8,
                        color: 'var(--color-accent, #fc9430)',
                        fontWeight: 600,
                      }}
                      title={`${summary!.totalErrorCount} transform error(s) across ${summary!.recordsWithErrors} of last ${summary!.windowSize} exports`}
                    >
                      ⚠ {summary!.recordsWithErrors}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* The empty-state in the right pane already provides the primary
          "+ Add destination" CTA when destinations.length === 0; suppressing
          the list-footer button here removes the duplicate affordance. (#48) */}
      {destinations.length > 0 && (
        <div className={styles.listFooter}>
          <button type="button" className={styles.addButton} onClick={onAdd}>
            + Add
          </button>
        </div>
      )}
    </>
  );
}
